import { useTranslation } from 'react-i18next';
import { useMonitoratge, type EstatPasarella } from '@/api/administracio';
import { Icona, type NomIcona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Buit, Kpi } from '@/pages/operacio/comu';
import './administracio.css';
import './sistema.css';

const INTEGRACIONS = ['ENTRA', 'ITCAU', 'MAILJET', 'TWILIO', 'PUSH'] as const;
type Integracio = (typeof INTEGRACIONS)[number];

/** Noms (o parts del nom) amb què el servidor pot identificar cada passarel·la. */
const CLAUS: Record<Integracio, string[]> = {
  ENTRA: ['entra', 'graph'],
  ITCAU: ['itcau', 'halo'],
  MAILJET: ['mailjet', 'email', 'correu'],
  TWILIO: ['twilio', 'trucada', 'veu', 'sms'],
  PUSH: ['push', 'apns', 'fcm', 'notification'],
};

const ICONA: Record<Integracio, [NomIcona, string]> = {
  ENTRA: ['shield-check', 'c-blue'],
  ITCAU: ['clipboard', 'c-orange'],
  MAILJET: ['envelope', 'c-blue'],
  TWILIO: ['phone', 'c-green'],
  PUSH: ['bell', 'c-purple'],
};

const BADGE_ESTAT: Record<string, string> = {
  OK: 'badge--ok',
  DEGRADAT: 'badge--incident',
  KO: 'badge--critic',
  NO_CONFIGURAT: 'badge--gray',
  DESCONEGUT: 'badge--gray',
};

/** La passarel·la pot portar la data de la darrera comprovació si el servidor la informa. */
type Passarella = EstatPasarella & { codi?: string | null; ultimaComprovacio?: string | null };

export function W29Integracions() {
  const { t } = useTranslation('administracio');
  const f = useFormat();
  const mon = useMonitoratge();
  const passarelles = (mon.data?.passarelles ?? []) as Passarella[];
  const passarella = (i: Integracio): Passarella | undefined =>
    passarelles.find((p) => (p.codi ?? '').toUpperCase() === i) ??
    passarelles.find((p) => CLAUS[i].some((c) => p.nom.toLowerCase().includes(c)));
  const ambProblemes = INTEGRACIONS.filter((i) => {
    const e = passarella(i)?.estat;
    return e === 'DEGRADAT' || e === 'KO';
  }).length;
  const senseConfigurar = INTEGRACIONS.filter((i) => passarella(i)?.estat === 'NO_CONFIGURAT').length;

  return (
    <section className="pantalla" data-screen="W-29">
      <Toolbar
        titol={t('titols.integracions')}
        subtitol={
          mon.data
            ? ambProblemes
              ? t('sistema.integracions.ambProblemes', { count: ambProblemes })
              : senseConfigurar
                ? t('sistema.integracions.senseConfigurar', { count: senseConfigurar })
                : t('sistema.integracions.totesOk')
            : undefined
        }
      >
        <button
          type="button"
          className="btn btn--outline btn--s"
          disabled={mon.isFetching}
          onClick={() => void mon.refetch()}
        >
          <Icona nom="arrow-clockwise" />
          {mon.isFetching ? t('integracions.provant') : t('integracions.prova')}
        </button>
      </Toolbar>
      <div className="content stack">
        {mon.isError ? (
          <div className="avis-banda" role="alert">
            <Icona nom="exclamationmark-triangle" />
            {t('sistema.integracions.errorMonitoratge')}
          </div>
        ) : null}
        <div className="panel sis-taula-scroll">
          <table className="taula">
            <thead>
              <tr>
                <th>{t('sistema.integracions.integracio')}</th>
                <th>{t('versions.estat')}</th>
                <th>{t('integracions.darreraComprovacio')}</th>
                <th className="num">{t('integracions.enviats')}</th>
                <th className="num">{t('integracions.fallits')}</th>
                <th>{t('integracions.ultimError')}</th>
              </tr>
            </thead>
            <tbody>
              {INTEGRACIONS.map((i) => {
                const p = passarella(i);
                const estat = p?.estat ?? 'DESCONEGUT';
                const [icona, color] = ICONA[i];
                return (
                  <tr key={i} data-integracio={i}>
                    <td>
                      <div className="sis-integracio">
                        <span className="sis-integracio__icona">
                          <Icona nom={icona} className={color} />
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <div className="strong">{t(`integracions.${i}.nom`)}</div>
                          <div className="t-caption dim">{t(`integracions.${i}.descripcio`)}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${BADGE_ESTAT[estat] ?? BADGE_ESTAT.DESCONEGUT}`}>
                        <i className={`dot sis-dot sis-dot--${estat.toLowerCase()}`} />
                        {t(`integracions.estat.${estat}`)}
                      </span>
                    </td>
                    <td className="dim nowrap">
                      {p?.ultimaComprovacio ? f.dataHora(p.ultimaComprovacio) : '—'}
                    </td>
                    <td className="num">{p ? f.numero(p.enviats) : '—'}</td>
                    <td className={`num${p && p.fallits > 0 ? ' c-red' : ''}`}>
                      {p ? f.numero(p.fallits) : '—'}
                    </td>
                    <td className={p?.ultimError ? 't-footnote' : 'dim'}>{p?.ultimError ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <h3 className="sis-subtitol">{t('sistema.integracions.rendiment')}</h3>
        {mon.isError ? (
          <ErrorCarrega onReintenta={() => void mon.refetch()} />
        ) : !mon.data ? (
          <Buit icona="waveform">
            {mon.isLoading ? t('comu.carregant') : t('sistema.integracions.senseDades')}
          </Buit>
        ) : (
          <div className="grid grid-3">
            <Kpi icona="tray" etiqueta={t('integracions.cua')} valor={f.numero(mon.data.cua)} />
            <Kpi
              icona="clock"
              etiqueta={t('integracions.latenciaP50')}
              valor={
                mon.data.latenciaP50 != null
                  ? t('integracions.ms', { n: mon.data.latenciaP50 })
                  : '—'
              }
            />
            <Kpi
              icona="clock-arrow"
              etiqueta={t('integracions.latenciaP95')}
              valor={
                mon.data.latenciaP95 != null
                  ? t('integracions.ms', { n: mon.data.latenciaP95 })
                  : '—'
              }
            />
          </div>
        )}
        <details className="sis-detalls">
          <summary>{t('sistema.integracions.configuracio')}</summary>
          <dl className="sis-dl">
            {INTEGRACIONS.map((i) => (
              <div key={i}>
                <dt>{t(`integracions.${i}.nom`)}</dt>
                <dd>{t(`integracions.${i}.config`)}</dd>
              </div>
            ))}
          </dl>
        </details>
        <p className="admin-nota">
          <Icona nom="lock" />
          {t('integracions.secrets')}
        </p>
      </div>
    </section>
  );
}
